import { Currency, DateTime, Icon, Level, MultilineText, Percent, Table } from '~/components';
import { useDateTimeFormat, useNumberFormat } from '~/hooks';
import _ from 'lodash';
import { rgba } from 'polished';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { colors, weights } from '~/styles';
import DraftStamp from '../assets/draft-stamp.svg?react';
import PaidStamp from '../assets/paid-stamp.svg?react';

const Page = styled.div`
  background-color: white;
  padding: 1.875rem 3.125rem;
  border: 1px solid ${colors.grey10};
  box-shadow: 0 6px 8px ${rgba(colors.black, 0.16)};

  @page {
    size: letter;
    margin-top: 1cm 0;
  }

  @media print {
    padding-top: 0;
    padding-bottom: 0;
    box-shadow: none;
    border: none;

    .table-box-body {
      display: block;
    }

    .table-box-row {
      page-break-inside: avoid;
    }
  }
`;

const Stamp = styled.div`
  position: absolute;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  height: 8.25rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid ${colors.grey10};
  position: relative;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  border-right: 1px solid ${colors.grey10};
  padding-right: 3.125rem;

  img {
    max-height: 5rem;
  }
`;

const Address = styled.div`
  &:not(:first-child) {
    padding-left: 3.125rem;
  }
`;

const Number = styled.div`
  margin-left: auto;
  font-size: 1.5rem;
  font-weight: ${weights.light};
`;

const InfoSection = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 2.875rem 0;
  border-bottom: 1px solid ${colors.grey10};

  dl {
    display: flex;
    flex: 1;
    margin-top: 0.875rem;

    dt {
      color: ${colors.grey40};
      text-transform: uppercase;
      font-weight: ${weights.black};
      font-size: 0.75rem;
      width: 8rem;
    }

    dd {
      font-size: 0.875rem;
    }
  }
`;

const InfoRight = styled.div`
  text-align: right;

  dl dt {
    width: 9rem;
  }

  dd {
    width: 8rem;
  }
`;

const LineItemsSection = styled.section`
  margin: 2.75rem 0;

  h2 {
    font-weight: ${weights.light};
    font-size: 1.375rem;
    margin-bottom: 1.125rem;
  }

  @media print {
    margin-bottom: 0;
  }
`;

const Footer = styled.div`
  display: flex;

  @media print {
    page-break-inside: avoid;
  }
`;

const Totals = styled.div`
  flex: 1;
  justify-content: flex-end;
  padding-right: 1rem;
`;

const Note = styled.div`
  padding: 2rem 2.5rem;
  flex: 1;

  dl {
    display: flex;

    dt {
      color: ${colors.grey40};
      text-transform: uppercase;
      font-weight: ${weights.black};
      font-size: 0.75rem;
      text-align: right;
      width: 2.5rem;
      margin-right: 2.5rem;
    }

    dd {
      font-size: 0.875rem;
    }
  }
`;

const PaymentsSection = styled.div`
  background-color: white;
  padding: 1.875rem 3.125rem;
  border: 1px solid ${colors.grey10};
  box-shadow: 0 6px 8px ${rgba(colors.black, 0.16)};
  margin-top: 2rem;

  @media print {
    display: none;
  }
`;

const TotalRow = styled(Level)`
  border: none;
  height: 3rem;
`;

const TotalLabel = styled(Level.Item)`
  text-align: right;
`;

const TotalValue = styled(Level.Item)`
  text-align: right;
  flex: 0;
  flex-basis: 8rem;
`;

const Separator = styled.hr`
  width: 20rem;
  margin-left: auto;
`;

const P = styled.p`
  white-space: pre-wrap;
`;

function Preview({ creditNote }) {
  const {
    client,
    currency,
    billTo,
    creditNoteFor,
    number,
    issuedOn,
    poNumber,
    notes,
    workspace,
    companyName,
    companyStreetAddress,
    invoices,
    appliedTotal,
    lines,
    taxRate,
    nonTaxableSubtotal,
    taxableSubtotal,
    tax,
    total,
    balance,
    displayColumns,
  } = creditNote;

  const isTaxable = lines.some((line) => line.taxable);
  const isDraft = creditNote.status.id === 'draft';
  const locale = client.locale ?? workspace.locale;

  const dateTimeFormat = useDateTimeFormat({ locale });
  const quantityFormat = useNumberFormat({ locale, minimumFractionDigits: 0 });

  const columns = useMemo(() => {
    const column = (name) => displayColumns.includes(name);

    return {
      item: {
        isVisible: column('item'),
        width: !column('details') ? undefined : '15rem',
      },
      details: { isVisible: column('details'), width: undefined },
      tax: {
        isVisible: column('tax'),
        width: !(column('details') || column('item')) ? undefined : '3rem',
      },
      quantity: {
        isVisible: column('quantity'),
        width: !(column('details') || column('item') || column('tax')) ? undefined : '5.5rem',
      },
      rate: {
        isVisible: column('rate'),
        width: !(column('details') || column('item') || column('tax') || column('quantity')) ? undefined : '8.5rem',
      },
      amount: { width: displayColumns.length === 0 ? undefined : '8rem' },
    };
  }, [displayColumns]);

  const logoUrl = useMemo(() => {
    const project = creditNote.projects?.[0];
    const client = creditNote.client;

    if (creditNote.projects?.length === 1 && project?.invoiceDetailsSource === 'business_unit') {
      return project.invoiceImageUrl;
    }

    if (client.invoiceDetailsSource === 'business_unit') {
      return client.invoiceImageUrl;
    }

    return workspace.invoiceImageUrl ?? workspace.imageUrl;
  }, [creditNote, workspace]);

  return (
    <>
      <Page>
        <Header>
          {workspace.includeInvoiceLogo && logoUrl && (
            <Logo>
              <img src={logoUrl} alt="logo" />
            </Logo>
          )}
          <Address>
            {companyName}
            <br />
            <MultilineText value={companyStreetAddress} align="left" />
          </Address>
          <Number>Credit Note # {number}</Number>
          {
            {
              draft: (
                <Stamp style={{ bottom: '-3.5rem', left: 'calc(50% - 177.03px / 2)' }}>
                  <DraftStamp />
                </Stamp>
              ),
              paid: (
                <Stamp style={{ bottom: '-2.25rem', left: 'calc(50% - 121.388px / 2)' }}>
                  <PaidStamp />
                </Stamp>
              ),
            }[creditNote.status.id]
          }
        </Header>
        <InfoSection>
          <div>
            <dl>
              <dt>Bill to</dt>
              <dd>
                <MultilineText value={billTo} />
              </dd>
            </dl>
            {creditNoteFor && (
              <dl>
                <dt>Credit Note For</dt>
                <dd>{creditNoteFor}</dd>
              </dl>
            )}
          </div>
          <InfoRight>
            <dl>
              <dt>Total Credit ({currency})</dt>
              <dd>
                <Currency value={total} currency={currency} locale={locale} />
              </dd>
            </dl>
            <dl>
              <dt>Issued On</dt>
              <dd>
                <DateTime value={issuedOn} locale={locale} />
              </dd>
            </dl>
            {poNumber && (
              <dl>
                <dt>Purchase Order #</dt>
                <dd>{poNumber}</dd>
              </dl>
            )}
          </InfoRight>
        </InfoSection>

        <LineItemsSection>
          <Table>
            <Table.BoxHeader style={{ background: colors.black, color: colors.white }}>
              <Table.Column isVisible={columns.item.isVisible} width={columns.item.width}>
                Item
              </Table.Column>
              <Table.Column isVisible={columns.details.isVisible} width={columns.details.width}>
                Details
              </Table.Column>
              <Table.Column isVisible={columns.tax.isVisible} align={columns.tax.align} width={columns.tax.width}>
                TAX
              </Table.Column>
              <Table.Column isVisible={columns.quantity.isVisible} align="right" width={columns.quantity.width}>
                QTY
              </Table.Column>
              <Table.Column isVisible={columns.rate.isVisible} align="right" width={columns.rate.width}>
                Rate
              </Table.Column>
              <Table.Column align="right" width={columns.amount.width}>
                Amount
              </Table.Column>
            </Table.BoxHeader>
            <Table.Body className="table-box-body">
              {lines.map((line) => {
                const item = isDraft ? line.invoiceItem?.name : line.item;

                return (
                  <Table.BoxRow key={line.id} className="table-box-row">
                    <Table.Cell>{item}</Table.Cell>

                    <Table.Cell>
                      <div>
                        <P>{line.description}</P>
                      </div>
                    </Table.Cell>

                    <Table.Cell align="center">{line.taxable && <Icon icon="check" />}</Table.Cell>

                    <Table.Cell align="right">{line.quantity ? quantityFormat.format(line.quantity) : ''}</Table.Cell>

                    <Table.Cell align="right">
                      <Currency
                        value={line.rate}
                        useDash={false}
                        maximumFractionDigits={7}
                        currency={currency}
                        locale={locale}
                      />
                    </Table.Cell>

                    <Table.Cell align="right">
                      <Currency value={line.amount} useDash={false} currency={currency} locale={locale} />
                    </Table.Cell>
                  </Table.BoxRow>
                );
              })}
            </Table.Body>
          </Table>

          <Footer>
            {notes && (
              <Note>
                <dl>
                  <dt>Note</dt>
                  <dd>
                    <P>{notes}</P>
                  </dd>
                </dl>
              </Note>
            )}

            <Totals>
              {isTaxable && (
                <>
                  <TotalRow>
                    <TotalLabel>Subtotal</TotalLabel>
                    <TotalValue>
                      <Currency value={_.sumBy(lines, 'amount')} currency={currency} locale={locale} />
                    </TotalValue>
                  </TotalRow>

                  <Separator />

                  <TotalRow>
                    <TotalLabel>Non-Taxable Subtotal</TotalLabel>
                    <TotalValue>
                      <Currency value={nonTaxableSubtotal} currency={currency} locale={locale} />
                    </TotalValue>
                  </TotalRow>

                  <TotalRow>
                    <TotalLabel>Taxable Subtotal</TotalLabel>
                    <TotalValue>
                      <Currency value={taxableSubtotal} currency={currency} locale={locale} />
                    </TotalValue>
                  </TotalRow>

                  <TotalRow>
                    <TotalLabel>
                      Tax{' '}
                      {taxRate !== null && (
                        <Percent
                          value={creditNote.taxRate / 100}
                          locale={locale}
                          minimumFractionDigits={0}
                          maximumFractionDigits={7}
                        />
                      )}
                    </TotalLabel>
                    <TotalValue>
                      <Currency value={tax} currency={currency} locale={locale} />
                    </TotalValue>
                  </TotalRow>

                  <Separator />
                </>
              )}

              <TotalRow style={{ fontWeight: weights.bold }}>
                <TotalLabel>Total Credit</TotalLabel>
                <TotalValue>
                  <Currency value={total} currency={currency} locale={locale} />
                </TotalValue>
              </TotalRow>
              {appliedTotal > 0 && (
                <TotalRow style={{ fontWeight: weights.bold }}>
                  <TotalLabel>Applied Credit</TotalLabel>
                  <TotalValue>
                    <Currency value={appliedTotal} currency={currency} locale={locale} />
                  </TotalValue>
                </TotalRow>
              )}
              <TotalRow style={{ fontWeight: weights.bold }}>
                <TotalLabel>Remaining Credit</TotalLabel>
                <TotalValue>
                  <Currency value={balance} currency={currency} locale={locale} />
                </TotalValue>
              </TotalRow>
            </Totals>
          </Footer>
        </LineItemsSection>
      </Page>

      {invoices?.length > 0 && (
        <PaymentsSection>
          <LineItemsSection>
            <h2>Invoices</h2>
            <Table>
              <Table.BoxHeader style={{ background: colors.black, color: colors.white }}>
                <Table.Column>Details</Table.Column>
                <Table.Column align="right" width="10rem">
                  Amount
                </Table.Column>
              </Table.BoxHeader>
              <Table.Body>
                {invoices.map((cni, index) => {
                  const details = _.compact([
                    dateTimeFormat.format(cni.invoice.issuedOn),
                    `#${cni.invoice.number}`,
                  ]).join(' - ');

                  return (
                    <Table.BoxRow key={index}>
                      <Table.Cell>{details}</Table.Cell>
                      <Table.Cell>
                        <Currency value={cni.amount} currency={currency} locale={locale} />
                      </Table.Cell>
                    </Table.BoxRow>
                  );
                })}
                <Table.Row style={{ borderBottom: 'none', fontWeight: weights.bold, textTransform: 'uppercase' }}>
                  <Table.Cell>Total</Table.Cell>
                  <Table.Cell>
                    <Currency value={_.sumBy(invoices, 'amount')} currency={currency} locale={locale} />
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>
          </LineItemsSection>
        </PaymentsSection>
      )}
    </>
  );
}

export default Preview;
